import React, { useState, useEffect, useRef } from "react";
import accountingReportsApi from "../../api/accountingReportsApi";
import chartOfAccountsApi from "../../api/chartOfAccountsApi";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import AccountStatementPrint from "./AccountStatementPrint";

export default function AccountStatement() {
  // ... existing state ...
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().slice(0, 8) + "01"
  );
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const printRef = useRef();

  useEffect(() => {
    fetchAccounts();
  }, []);

  // ... fetchAccounts and handleSearch ...
  const fetchAccounts = async () => {
    try {
      // Use getAll to allow searching any account, but UI can highlight postable ones
      const res = await chartOfAccountsApi.getAll({ includeInactive: false });
      setAccounts(res.data);
    } catch (error) {
      console.error("Failed to fetch accounts", error);
      toast.error("فشل تحميل الحسابات");
    }
  };

  const handleSearch = async () => {
    if (!selectedAccount) {
      toast.warning("الرجاء اختيار الحساب");
      return;
    }
    setLoading(true);
    try {
      const res = await accountingReportsApi.getStatement({
        accountId: selectedAccount.id,
        fromDate,
        toDate,
      });
      setReportData(res.data);
    } catch (error) {
      console.error(error);
      toast.error("فشل جلب كشف الحساب");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Statement-${selectedAccount?.code}-${new Date().toISOString()}`,
  });

  const SummaryCard = ({ title, value, color, icon }) => (
    <Card
      sx={{
        bgcolor: color,
        color: "white",
        height: "100%",
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        {icon}
        <Typography variant="h6" fontWeight="bold" mt={1}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value?.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box p={3} dir="rtl">
      <Typography variant="h4" fontWeight="bold" mb={3} color="primary">
        📂 كشف حساب (Account Statement)
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Account Selection - Wider */}
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.code} - ${option.nameAr}`}
                value={selectedAccount}
                onChange={(event, newValue) => setSelectedAccount(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="بحث عن حساب"
                    variant="outlined"
                    fullWidth
                    size="small"
                    helperText="يمكنك البحث برقم الحساب أو الاسم"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {option.code} - {option.nameAr}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.accountTypeText} - {option.allowPosting ? "يقبل القيد" : "رئيسي"}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Date Range - Compact */}
            <Grid item xs={6} md={2.5}>
              <TextField
                label="من تاريخ"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField
                label="إلى تاريخ"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={2} display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                fullWidth
                sx={{ height: 40 }}
              >
                {loading ? "..." : "بحث"}
              </Button>
              {reportData && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handlePrint}
                  sx={{ height: 40, minWidth: 40 }}
                >
                  <PrintIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Hidden Print Component */}
      <div style={{ display: 'none' }}>
        <AccountStatementPrint 
            ref={printRef} 
            reportData={reportData}
            fromDate={fromDate}
            toDate={toDate}
        />
      </div>

      {reportData && (
        <div style={{ padding: "0 0 20px 0" }}>
          {/* Summary Cards - Horizontal & Compact - Screen Only */}
          <Grid container spacing={3} mb={3} alignItems="flex-start">
            <Grid item xs={12}>
              <Grid container spacing={1}>
                 <Grid item xs={3}>
                    <SummaryCard 
                        title="رصيد افتتاحي"
                        value={reportData.openingBalance}
                        color="#607d8b"
                        icon={<AccountBalanceIcon sx={{ fontSize: 24, mb: 0.5 }} />}
                    />
                 </Grid>
                 <Grid item xs={3}>
                    <SummaryCard 
                        title="إجمالي مدين"
                        value={reportData.totalDebit}
                        color="#4caf50"
                        icon={<TrendingUpIcon sx={{ fontSize: 24, mb: 0.5 }} />}
                    />
                 </Grid>
                 <Grid item xs={3}>
                    <SummaryCard 
                        title="إجمالي دائن"
                        value={reportData.totalCredit}
                        color="#f44336"
                        icon={<TrendingDownIcon sx={{ fontSize: 24, mb: 0.5 }} />}
                    />
                 </Grid>
                 <Grid item xs={3}>
                    <SummaryCard 
                        title="رصيد ختامي"
                        value={reportData.closingBalance}
                        color="#2196f3"
                        icon={<AccountBalanceWalletIcon sx={{ fontSize: 24, mb: 0.5 }} />}
                    />
                 </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Data Table */}
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
            <Table sx={{ minWidth: 650 }} size="medium">
              <TableHead sx={{ bgcolor: "#eeeeee" }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>#</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>التاريخ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>نوع القيد</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }} width="35%">البيان</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#2e7d32" }}>مدين</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#c62828" }}>دائن</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#1565c0" }}>الرصيد</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening Balance Row */}
                <TableRow sx={{ bgcolor: "#fff3e0" }}>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="center">{new Date(fromDate).toLocaleDateString("en-GB")}</TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>رصيد افتتاحي</TableCell>
                  <TableCell align="center">
                    {reportData.openingBalance > 0 ? reportData.openingBalance.toLocaleString() : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {reportData.openingBalance < 0 ? Math.abs(reportData.openingBalance).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {reportData.openingBalance.toLocaleString()}
                  </TableCell>
                </TableRow>

                {reportData.lines.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:nth-of-type(odd)": { bgcolor: "#f9f9f9" }, "&:hover": { bgcolor: "#e3f2fd" } }}
                  >
                    <TableCell align="center" sx={{ fontSize: "1rem" }}>{row.entryNumber}</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1rem" }}>{new Date(row.date).toLocaleDateString("en-GB")}</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1rem" }}>{row.entryType}</TableCell>
                    <TableCell sx={{ fontSize: "1rem" }}>{row.description}</TableCell>
                    <TableCell align="center" sx={{ color: "#2e7d32", fontWeight: row.debit > 0 ? "bold" : "normal", fontSize: "1rem" }}>
                      {row.debit > 0 ? row.debit.toLocaleString() : "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "#c62828", fontWeight: row.credit > 0 ? "bold" : "normal", fontSize: "1rem" }}>
                      {row.credit > 0 ? row.credit.toLocaleString() : "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#1565c0", fontSize: "1rem" }}>
                      {row.balance.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}

                {reportData.lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, fontSize: "1.2rem", color: "text.secondary" }}>
                      لا توجد حركات خلال الفترة المحددة
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </Box>
  );
}
